import { ChangeDetectionStrategy, Component, computed, OnInit, signal, inject } from '@angular/core';
import {
    AuthService,
    BlockAbstractComponent,
    ParserService,
    BlockDownloadPdfComponent,
} from '@gallaghercp/core-frontend';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';

interface UserDocument {
    id: number;
    name: string;
    url: string;
    type: string;
    uploaded: string;
    interactions?: any[];
}

@Component({
    selector: 'core-block-user-documents',
    templateUrl: './block-user-documents.component.html',
    styleUrls: ['./block-user-documents.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BlockUserDocumentsComponent extends BlockAbstractComponent implements OnInit {
    protected override parser: ParserService;
    protected override auth: AuthService;
    protected http = inject(HttpClient);
    private modalService = inject(BsModalService);

    currentDocPage = signal(1);
    pageSize = 3;
    userDocuments = signal<UserDocument[]>([]);
    isMocked = signal(false); // Set to true to use the 10-item mock data
    modalRef?: BsModalRef;

    private mockData = Array.from({ length: 10 }, (_, i) => ({
        collectionId: 4,
        dateCreated: 1771246390 - i * 86400, // Different dates
        dateModified: 1773396741,
        id: 34 + i,
        identifiers: { YEAR: 2024 - Math.floor(i / 2) },
        name: `Pension Statement ${2024 - Math.floor(i / 2)} test_${i + 1}.pdf`,
        interactions: [
            {
                data: 'JVBERi0xLjAKMSAwIG9iaiA8PCAvVHlwZSAvQ2F0YWxvZyAvUGFnZXMgMiAwIFIgPj4gZW5kb2JqIDIgMCBvYmogPDwgL1R5cGUgL1BhZ2VzIC9LaWRzIFsgMyAwIFIgXSAvQ291bnQgMSA+PiBlbmRvYmogMyAwIG9iaiA8PCAvVHlwZSAvUGFnZSAvUGFyZW50IDIgMCBSIC9NZWRpYUJveCBbIDAgMCA1OTUgODQyIF0gPj4gZW5kb2JqIHhyZWYgMCA0IDAwMDAwMDAwMDAgNjU1MzUgZiAwMDAwMDAwMDA5IDAwMDAwIG4gMDAwMDAwMDA1MiAwMDAwMCBuIDAwMDAwMDAxMDEgMDAwMDAgbiB0cmFpbGVyIDw8IC9TaXplIDQgL1Jvb3QgMSAwIFIgPj4gc3RhcnR4cmVmIDE3OCAlJUVPRgo=',
            },
        ],
    }));

    constructor() {
        const parser = inject(ParserService);
        const auth = inject(AuthService);

        super(parser, auth);

        this.parser = parser;
        this.auth = auth;
    }

    totalPages = computed(() => Math.ceil(this.userDocuments().length / this.pageSize));

    truncatedDocumentList = computed(() => {
        const docs = this.userDocuments();
        const start = (this.currentDocPage() - 1) * this.pageSize;
        return docs.slice(start, start + this.pageSize);
    });

    setPage(page: number) {
        if (page >= 1 && page <= this.totalPages()) {
            this.currentDocPage.set(page);
        }
    }

    override ngOnInit(): void {
        super.ngOnInit();
        const collection = this.block.data.chosenCollection;
        if (collection) {
            this.loadUserDocuments(collection);
        }
    }

    private loadUserDocuments(collectionTypeId: number): void {
        const data$ = this.isMocked()
            ? new Observable(subscriber => {
                  subscriber.next(this.mockData);
                  subscriber.complete();
              })
            : this.getUserCollections(collectionTypeId, null, true);

        data$
            .pipe(
                map((collections: any) => (Array.isArray(collections) ? collections : [])),
                map((collections: any[]) => this.processUserDocuments(collections)),
            )
            .subscribe({
                next: (documents: any) => {
                    this.userDocuments.set(documents);
                    this.currentDocPage.set(1); // Reset to first page on load
                },
                error: err => console.error('Error loading user documents:', err),
            });
    }

    private processUserDocuments(collections: any[]): UserDocument[] {
        return collections.map(item => {
            const date = item.dateCreated ? new Date(item.dateCreated * 1000) : new Date();
            const formattedDate = date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });

            let name = item.name || 'Unnamed Document';
            // Strip folder names from the document title (both / and \ separators)
            name = name.split(/[\\/]/).pop() || name;

            return {
                id: item.collectionId,
                name: name,
                url: '#',
                type: 'PDF',
                uploaded: formattedDate,
                interactions: item.interactions,
            };
        });
    }

    downloadDocument(document: UserDocument) {
        this.http.get(`user-collection-download/${document.id}`, { responseType: 'text' }).subscribe({
            next: (base64Data: string) => {
                if (base64Data) {
                    try {
                        const blob = this.base64ToBlob(base64Data, 'application/pdf');
                        const rawUrl = window.URL.createObjectURL(blob);

                        this.modalRef = this.modalService.show(BlockDownloadPdfComponent, {
                            class: 'modal-lg modal-pdf',
                            initialState: {
                                blobUrl: rawUrl,
                                fileUrl: rawUrl,
                                fileName: document.name,
                            },
                        });

                        // User documents are stored as base64 strings in the database so we are hijacking the 'View file in new tab' click to open the clean blob URL instead without '?i=1' in the fileUrl.
                        setTimeout(() => {
                            const link = window.document.querySelector('.modal-pdf .link-ext');
                            if (link instanceof HTMLAnchorElement) {
                                link.addEventListener('click', (e: Event) => {
                                    e.preventDefault();
                                    window.open(rawUrl, '_blank');
                                });
                            }
                        }, 100);
                    } catch (e) {
                        console.error('Error creating PDF blob:', e);
                    }
                }
            },
            error: err => console.error('Error fetching individual collection:', err),
        });
    }

    private base64ToBlob(b64Data: string, contentType: string, sliceSize = 512) {
        const byteCharacters = atob(b64Data);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
        }
        return new Blob(byteArrays, { type: contentType });
    }

    getUserCollections(typeId: number, identifiers?: unknown | null, includeContent?: boolean) {
        let params = new HttpParams().set('content', includeContent ? 'true' : 'false');
        if (identifiers !== null && identifiers !== undefined) {
            params = params.set('identifiers', JSON.stringify(identifiers || {}));
        }
        params = params.set('interact', 'false').set('typeId', typeId);
        return this.http.get('user-collections', { params });
    }
}
